import type { ReactNode } from "react";
import {
  SlidersHorizontal,
  Sprout,
  CloudSun,
  Cable,
  Radio,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { C, FONT, card } from "../theme";
import { priceLevelColor, priceLevelLabel } from "../lib/price";
import { isSimulated } from "../lib/sources";
import type { DashboardState } from "../types";

/** A live/simulated provenance row with a status dot. */
function SourceRow({ label, value, last }: { label: string; value: string; last?: boolean }) {
  const simulated = isSimulated(value);
  const color = simulated ? C.beacon : C.sageDeep;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        padding: "11px 0",
        borderBottom: last ? "none" : `1px solid ${C.lineSoft}`,
      }}
    >
      <span style={{ fontSize: 13, color: C.ink }}>{label}</span>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: 12, color }}>
        <span style={{ width: 7, height: 7, borderRadius: 99, background: color }} className={simulated ? undefined : "beacon-pulse"} />
        {value}
      </span>
    </div>
  );
}

/** One label/value row inside a settings card, with a hairline separator below all but the last. */
function Row({
  label,
  value,
  valueColor,
  mono,
  last,
}: {
  label: string;
  value: string;
  valueColor: string;
  mono?: boolean;
  last?: boolean;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 14,
        padding: "11px 0",
        borderBottom: last ? "none" : `1px solid ${C.lineSoft}`,
      }}
    >
      <span style={{ fontSize: 13, color: C.ink }}>{label}</span>
      <span
        className={mono ? "mono" : undefined}
        style={{
          fontFamily: mono ? FONT.mono : undefined,
          fontSize: mono ? 11.5 : 12.5,
          color: valueColor,
          textAlign: mono ? "right" : undefined,
          maxWidth: mono ? 210 : undefined,
        }}
      >
        {value}
      </span>
    </div>
  );
}

/** A titled settings card: Newsreader 17 title with its lucide icon, then a stack of rows. */
function SettingCard({
  title,
  Icon,
  iconColor,
  children,
}: {
  title: string;
  Icon: LucideIcon;
  iconColor: string;
  children: ReactNode;
}) {
  return (
    <div style={{ ...card, padding: 22 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
        <Icon size={17} style={{ color: iconColor }} />
        <span style={{ fontFamily: FONT.serif, fontSize: 17, fontWeight: 500, color: C.ink }}>
          {title}
        </span>
      </div>
      {children}
    </div>
  );
}

/** Settings view — 2×2 card grid: skip rules, soil profile, weather sources, controller. */
export function Settings({ state }: { state: DashboardState }) {
  const { rules, syncedMinsAgo, controller, sources, price } = state;
  const src = sources ?? {
    weather: "Open-Meteo · live",
    soil: "Model (ET-driven) · simulated",
    valves: "Simulated",
    flow: "Simulated",
    pump: "Model (valve-driven) · simulated",
  };

  return (
    <div
      className="marram-settings"
      style={{ display: "grid", gridTemplateColumns: "1fr", gap: 18 }}
    >
      <style>{`@media (min-width: 760px){.marram-settings{grid-template-columns:repeat(2,1fr) !important}.marram-settings .span2{grid-column:1 / -1}}`}</style>

      <div className="span2">
        <SettingCard title="Data sources" Icon={Radio} iconColor={C.sage}>
          <div style={{ fontSize: 12, color: C.textMid, marginTop: -2, marginBottom: 4 }}>
            Weather and the ET₀ balance run on live data now. Sensors and valves are simulated —
            each switches to real automatically when its Home Assistant entity comes online.
          </div>
          <SourceRow label="Weather / ET₀" value={src.weather} />
          <SourceRow label="Soil moisture" value={src.soil} />
          <SourceRow label="Valves" value={src.valves} />
          <SourceRow label="Flow meter" value={src.flow} />
          <SourceRow label="Pump / pressure" value={src.pump ?? "Model (valve-driven) · simulated"} />
          <SourceRow label="Power price" value={src.power ?? (price?.enabled ? "Nord Pool SE4 · live" : "Off")} last />
        </SettingCard>
      </div>

      <SettingCard title="Skip rules" Icon={SlidersHorizontal} iconColor={C.sea}>
        <Row label="Rain skip" value={`over ${rules.rainMm} mm forecast / 24h`} valueColor={C.ice} mono />
        <Row label="Freeze hold" value={`at/below ${rules.freezeC} °C`} valueColor={C.ice} mono />
        <Row
          label="Wind hold · sprinklers"
          value={`over ${rules.windMs} m/s`}
          valueColor={C.beacon}
          mono
          last
        />
      </SettingCard>

      <SettingCard title="Power price" Icon={Zap} iconColor={C.beacon}>
        {price?.enabled ? (
          <>
            <Row
              label="Now"
              value={
                price.ok && price.now != null
                  ? `${price.now.toFixed(2)} kr/kWh · ${priceLevelLabel(price.level).toLowerCase()}`
                  : "Unavailable — watering on weather alone"
              }
              valueColor={price.ok ? priceLevelColor(price.level) : C.textMid}
              mono
            />
            <Row
              label="Lawn sprinklers"
              value={`skip ≥ ${priceLevelLabel(price.skip.spray).toLowerCase()} · ${price.windows.spray.replace("-", "–")}`}
              valueColor={C.textMid}
              mono
            />
            <Row
              label="Drip beds"
              value={`skip ≥ ${priceLevelLabel(price.skip.drip).toLowerCase()} · ${price.windows.drip.replace("-", "–")}`}
              valueColor={C.textMid}
              mono
            />
            <Row label="Source" value={`${price.source} · ${price.updatedMins} min ago`} valueColor={C.textMid} mono last />
          </>
        ) : (
          <Row label="Price gating" value="off — watering on weather only" valueColor={C.textMid} last />
        )}
      </SettingCard>

      <SettingCard title="Soil profile" Icon={Sprout} iconColor={C.sage}>
        <Row label="Soil type" value="Sandy · fast-draining" valueColor={C.textMid} />
        <Row label="Available water" value="low — small reservoir" valueColor={C.textMid} />
        <Row label="Strategy" value="frequent short cycles" valueColor={C.textMid} />
        <Row label="Field capacity" value="≈14% VWC (100% available)" valueColor={C.textMid} last />
      </SettingCard>

      <SettingCard title="Weather sources" Icon={CloudSun} iconColor={C.ice}>
        <Row label="Open-Meteo" value="ET₀, soil, forecast — primary" valueColor={C.textMid} />
        <Row label="SMHI" value="local Falsterbo cross-check" valueColor={C.textMid} />
        <Row label="ET₀ demand" value="evapotranspiration · daily water loss to sun + wind (mm)" valueColor={C.textMid} />
        <Row label="Last sync" value={`${syncedMinsAgo} min ago`} valueColor={C.textMid} last />
      </SettingCard>

      <SettingCard title="Controller" Icon={Cable} iconColor={C.textMid}>
        <Row label="Board" value={controller.board} valueColor={C.textMid} mono />
        <Row label="Network" value={controller.network} valueColor={C.textMid} mono />
        <Row label="Outputs" value={controller.outputs} valueColor={C.textMid} mono />
        <Row label="Sensors" value={controller.sensors} valueColor={C.textMid} mono last />
      </SettingCard>
    </div>
  );
}
